import { useEffect, useState } from 'react'
import { ACTIVITY_ACTION_NAME } from 'const/common'
import { useLogger } from './Logger.Utility'
import packageObject from '../../../package.json'

// Version comparison function
const needToReFetch = ({
	latestVersion,
	currentVersion,
}: {
	latestVersion: string
	currentVersion: string
}): boolean => {
	const versionsA = latestVersion.split(/\./g)
	const versionsB = currentVersion.split(/\./g)
	while (versionsA.length || versionsB.length) {
		const a = Number(versionsA.shift())
		const b = Number(versionsB.shift())
		if (a === b) continue
		return a > b || isNaN(b)
	}
	return false
}

const refreshCacheAndReload = async () => {
	try {
		if (window?.caches) {
			const { caches } = window
			const cacheNames = await caches.keys()
			const cacheDeletionPromises = cacheNames.map((n) => caches.delete(n))

			await Promise.all(cacheDeletionPromises)

			// @ts-ignore: Firefox still has a `forceReload` parameter.
			window.location.reload(true)

			//  In case if it create issue then use below code
			// const cacheBustedUrl =
			// 	window.location.origin + window.location.pathname + '?nocache=' + new Date().getTime()
			// window.location.href = cacheBustedUrl
		}
	} catch (error) {
		console.error('Error refreshing cache and reloading:', error)
	}
}

const usePageLogger = () => {
	const { onCallLoggerFunction, location } = useLogger()
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const apiUrl = process.env.REACT_APP_API_URL ?? ''
	const cacheBustAndLogActivity = !apiUrl.includes('localhost')

	// Fetch the version and check if an update is needed
	const fetchVersion = async () => {
		try {
			const response = await fetch(`/meta.json`, {
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
				},
			})
			const meta = await response.json()
			const latestVersion = meta.version
			const currentVersion = packageObject?.version
			const shouldForceRefresh = needToReFetch({ latestVersion, currentVersion })
			if (shouldForceRefresh) {
				setIsLoading(true)
				refreshCacheAndReload()
			}
		} catch (error) {
			console.error('Error fetching version info:', error)
		} finally {
			setIsLoading(false)
		}
	}

	// Run version check and page visit logging when pathname changes
	useEffect(() => {
		if (cacheBustAndLogActivity) {
			fetchVersion()
			onCallLoggerFunction({ actionName: ACTIVITY_ACTION_NAME?.PAGE_VISIT })
		}
	}, [location?.pathname])

	// Run version check and filter section activity logging when search query changes
	useEffect(() => {
		if (location?.search && cacheBustAndLogActivity) {
			onCallLoggerFunction({ actionName: ACTIVITY_ACTION_NAME?.FILTER_SECTION_ACTIVITY })
		}
	}, [location?.search])

	return isLoading
}

export default usePageLogger
